import React from 'react';
import { motion } from 'framer-motion';
import { CompassIcon, BriefcaseIcon, GraduationCapIcon, CheckCircle2Icon, CalendarIcon, AwardIcon, TargetIcon, UsersIcon, MicIcon, MessageCircleIcon, HeartIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Container } from '../../components/ui/Container';
import { Button } from '../../components/ui/Button';
import { SubpageHero } from '../../components/sections/subpages/SubpageHero';
import { PatternDecor } from '../../components/ui/PatternDecor';
const pathways = [{
  icon: CompassIcon,
  title: 'Muslim Life Fellowship',
  body: 'A 12-month applied journey across identity, family life, professional responsibilities, and community impact — for adults seeking clarity, balance, and growth.',
  to: '#muslim-life-fellowship'
}, {
  icon: BriefcaseIcon,
  title: 'Professional Muslim Leadership Track',
  body: 'For professionals, entrepreneurs, educators, and leaders who want to lead ethically and engage confidently in professional and public spaces.',
  to: '#professional-leadership'
}, {
  icon: GraduationCapIcon,
  title: 'Educator & Mentor Path',
  body: 'Annual certification for educators, mentors, and community workers — covering child development, safeguarding, and ethical teaching.',
  to: '#educator-mentor'
}];
const features = [{
  icon: CalendarIcon,
  title: '12-month structured pathway',
  body: 'Monthly themes, live sessions, and personal reflection prompts.'
}, {
  icon: TargetIcon,
  title: 'Clear outcomes',
  body: 'Each pathway has defined goals you progress toward, month by month.'
}, {
  icon: AwardIcon,
  title: 'Certificate of completion',
  body: 'Recognition for completing the full 12-month journey.'
}];
export function AnnualPrograms() {
  return <>
      <SubpageHero eyebrow="Structured 12-month pathways for Muslim families" title="Annual Programs" paragraphs={['Guided reflection and practical skills for identity, family life, professional responsibilities, and community impact — grounded in Islamic values and adapted to contemporary realities.']} primaryCta={{
      label: 'Explore Programs',
      to: '#programs'
    }} secondaryCta={{
      label: 'Request Info',
      to: '/en/contact'
    }} image="https://cdn.magicpatterns.com/uploads/2oyAijm6fxaEJiR4qbNAKH/AnnualPrograms6.jpg" imageAlt="Muslim woman reviewing a Muslim Life Fellowship annual growth roadmap booklet at her desk" compact masked data-id="element-987" data-mp="1cbjvoyh0trg9" />

      {/* Choose your annual pathway */}
      <section id="programs" className="py-16 md:py-24 scroll-mt-24 bg-sage-50/60" data-id="element-988" data-mp="2zjid12xtv3hy">
        <Container data-id="element-989" data-mp="2bto0ynlc1iyr">
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-990" data-mp="2bt4tk0tb6cyz">
            Choose your annual pathway
          </h2>
          <p className="mt-5 text-ink-700 leading-relaxed" data-id="element-991" data-mp="mdankjx1v1tq">
            All annual programs are delivered online and designed as structured
            12-month journeys with clear outcomes and certificates of
            completion.
          </p>
          <div className="mt-8 rounded-2xl bg-white border border-sage-200 px-6 py-4 text-center" data-id="element-992" data-mp="ddno43nvs63b">
            <p className="text-ink-500 italic" data-id="element-993" data-mp="hf0rm73i3x72">
              This is not academic study. It is guided reflection, applied
              learning, and personal growth.
            </p>
          </div>

          <div className="mt-10 grid md:grid-cols-3 gap-5" data-id="element-994" data-mp="1a1lil6g2e7a3">
            {pathways.map((p, i) => <motion.div key={p.title} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.5,
            delay: i * 0.06,
            ease: [0.22, 1, 0.36, 1]
          }} whileHover={{
            y: -2
          }} className="rounded-2xl" data-id="element-995" data-mp="w2c0u2c7n540" data-mpi={i}>
                <Link to={p.to} className={`group block h-full rounded-2xl border border-ink-900/5 p-6 shadow-soft transition-shadow hover:shadow-card ${['bg-sage-100', 'bg-[#F7EDE9]', 'bg-[#EDF2F7]'][i % 3]}`} data-id="element-996" data-mp="3naxqkzrrqneu">
                  <span className="w-11 h-11 inline-flex items-center justify-center rounded-xl bg-white border border-forest-700/10 text-forest-700" data-id="element-997" data-mp="iud3tpsh99tc">
                    <p.icon size={18} strokeWidth={1.6} data-id="element-998" data-mp="9wksewpvmmb8" />
                  </span>
                  <h3 className="mt-5 font-display text-lg text-ink-900" data-id="element-999" data-mp="2zlmptiaf4t0p">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-ink-700 text-sm leading-relaxed" data-id="element-1000" data-mp="2ul7dmu1vhrkb">
                    {p.body}
                  </p>
                </Link>
              </motion.div>)}
          </div>

          <div className="mt-8 flex flex-col gap-2 rounded-2xl border border-sage-200 bg-white px-6 py-5 sm:flex-row sm:items-center" data-id="element-1001" data-mp="1f3ucne1e0vk4">
            <span className="inline-flex w-fit whitespace-nowrap rounded-full bg-sage-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700" data-id="element-1002" data-mp="1j2tdonk0086a">
              Coming soon
            </span>
            <p className="flex-1 text-center text-sm font-semibold text-ink-700" data-id="element-1003" data-mp="1xtady3kwcs1l">
              The names of our confirmed teachers, educators, and Shariah Board
              members will be announced soon.
            </p>
          </div>
        </Container>
      </section>

      {/* Muslim Life Fellowship */}
      <section id="muslim-life-fellowship" className="py-16 md:py-24 scroll-mt-24 bg-white/60" data-id="element-1004" data-mp="8f46dnd1awn3">
        <Container data-id="element-1005" data-mp="ch8wic49xb4z">
          <div className="rounded-3xl bg-white border border-sage-200 shadow-soft overflow-hidden" data-id="element-1006" data-mp="cgpp3pc92557">
            {/* Header Band */}
            <div className="px-7 md:px-10 py-8 bg-sage-50 border-b border-sage-200 flex flex-col md:flex-row md:items-center justify-between gap-6" data-id="element-1007" data-mp="1as0e6pk9bgg0">
              <div data-id="element-1008" data-mp="1osky5p1wrib3">
                <div className="flex items-center gap-3 mb-3" data-id="element-1009" data-mp="1ot4gh6vh08pn">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-forest-100 text-forest-800 text-xs font-semibold tracking-wide" data-id="element-1010" data-mp="1kpyam0c9sjjp">
                    12 Months
                  </span>
                  <span className="text-ink-500 text-sm font-medium" data-id="element-1011" data-mp="1tpuyhzqu5edp">
                    Annual Applied Program
                  </span>
                </div>
                <h2 className="font-display text-3xl md:text-4xl text-ink-900" data-id="element-1012" data-mp="23gpgut0ynttv">
                  Muslim Life Fellowship
                </h2>
              </div>
              <div className="flex flex-col md:items-end gap-3" data-id="element-1013" data-mp="2hh0dkcoyc2my">
                <div className="text-right" data-id="element-1014" data-mp="2ci5k8gtcyvol">
                  <p className="text-2xl font-display text-forest-700" data-id="element-1015" data-mp="2cfaymlylz4tl">
                    €1,200{' '}
                    <span className="text-base text-ink-500 font-sans" data-id="element-1016" data-mp="2w1354dsehyal">
                      / year
                    </span>
                  </p>
                </div>
                <Link to="/waitlist" data-id="element-1017" data-mp="2n1y3uzpgfquq">
                  <Button variant="primary" data-id="element-1018" data-mp="2r42v5rsfkbe7">Reserve your seat</Button>
                </Link>
              </div>
            </div>

            <div className="grid lg:grid-cols-12 gap-0" data-id="element-1019" data-mp="2dcek7rtx3syt">
              {/* Left Column: Content */}
              <div className="lg:col-span-7 px-7 md:px-10 py-10 border-b lg:border-b-0 lg:border-r border-sage-200" data-id="element-1020" data-mp="2i3o74ko30i70">
                {/* 1. Outcome-first framing */}
                <h3 className="text-xl md:text-2xl font-display text-ink-900 leading-snug" data-id="element-1021" data-mp="28dl55uyzftwj">
                  In 12 months, move from uncertainty to clarity — confident in
                  your identity, family life, and work, without compromising
                  your values.
                </h3>
                <p className="mt-4 text-ink-700 leading-relaxed" data-id="element-1022" data-mp="2euwnuwezwjjz">
                  The Muslim Life Fellowship is a year-long applied learning
                  journey designed for Muslim adults living in non-Muslim
                  countries. It supports participants in developing a mature,
                  confident, and balanced approach to modern life.
                </p>
                <p className="mt-3 text-ink-500 italic text-sm" data-id="element-1023" data-mp="20x827md3t8dv">
                  This is not academic study — it is guided reflection, applied
                  learning, and personal growth.
                </p>

                {/* 3a. Who it's for (Chips) */}
                <div className="mt-10" data-id="element-1024" data-mp="1m70uo1omx9ry">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-4" data-id="element-1025" data-mp="1m3whwfx3t5po">
                    Who this is for
                  </h4>
                  <div className="flex flex-wrap gap-2" data-id="element-1026" data-mp="1d51d989pk3oh">
                    {['Muslim adults living in non-Muslim countries', 'Parents seeking clarity and security', 'Professionals navigating ethical & social challenges', 'Individuals seeking long-term guidance and prosperity'].map(chip => <span key={chip} className="inline-flex items-center px-3 py-1.5 rounded-lg bg-sand-50 border border-sand-200 text-ink-700 text-sm" data-id="element-1027" data-mp="3svax3dafdzfw">
                        {chip}
                      </span>)}
                  </div>
                </div>

                {/* 3b. Format (Icon row) */}
                <div className="mt-10" data-id="element-1028" data-mp="3a2p5j56jwv14">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-4" data-id="element-1029" data-mp="3eugp2vrai0hr">
                    Format
                  </h4>
                  <div className="grid sm:grid-cols-2 gap-4" data-id="element-1030" data-mp="2hf2u5ar30m3p">
                    {[{
                    icon: UsersIcon,
                    text: '2 live group sessions / month'
                  }, {
                    icon: CalendarIcon,
                    text: 'Monthly guided reflection'
                  }, {
                    icon: MicIcon,
                    text: 'Guest speakers & practitioners'
                  }, {
                    icon: MessageCircleIcon,
                    text: 'Peer discussion groups'
                  }].map(f => <div key={f.text} className="flex items-center gap-3" data-id="element-1031" data-mp="27je8vo632zgk">
                        <span className="w-8 h-8 rounded-full bg-sage-100 text-forest-700 flex items-center justify-center flex-shrink-0" data-id="element-1032" data-mp="27hb94cf4pryn">
                          <f.icon size={14} strokeWidth={2} data-id="element-1033" data-mp="1ev8ncz77h6fo" />
                        </span>
                        <span className="text-sm text-ink-700" data-id="element-1034" data-mp="1jt0cxmkdoggp">{f.text}</span>
                      </div>)}
                  </div>
                </div>

                {/* 3c. Outcomes */}
                <div className="mt-10 bg-sage-50 rounded-2xl p-6 border border-sage-200" data-id="element-1035" data-mp="17aoc5botea25">
                  <h4 className="font-display text-lg text-ink-900 mb-4" data-id="element-1036" data-mp="12bbmkj6k6ovv">
                    Where you'll be in 12 months
                  </h4>
                  <ul className="space-y-3" data-id="element-1037" data-mp="xcia8u30d3xa">
                    {['Greater clarity and confidence', 'Practical tools for family and professional life', 'Stronger sense of responsibility and purpose', 'Certificate of completion'].map(item => <li key={item} className="flex items-start gap-3 text-ink-800 font-medium" data-id="element-1038" data-mp="en1x97a4zizz">
                        <CheckCircle2Icon size={18} className="text-forest-700 mt-0.5 flex-shrink-0" strokeWidth={2} data-id="element-1039" data-mp="5lkhjq0n5jbt" />
                        {item}
                      </li>)}
                  </ul>
                </div>
              </div>

              {/* Right Column: Timeline & Pricing */}
              <div className="lg:col-span-5 bg-[#FAFAFA]" data-id="element-1040" data-mp="3n3rijhitumk5">
                <div className="px-7 md:px-10 py-10" data-id="element-1041" data-mp="3n6doxdtzew0s">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-6" data-id="element-1042" data-mp="epiveoz2qnzd">
                    Program Journey
                  </h4>

                  {/* 4. Quarter Timeline */}
                  <div className="relative border-l border-sage-200 ml-3 space-y-8 pb-4" data-id="element-1043" data-mp="4xv6ovtwjl1l">
                    {[{
                    q: 'Q1',
                    title: 'Identity & Values',
                    topics: ['Islamic values in everyday life', 'Adab and ethical conduct', 'Applied fiqh for real-life situations', 'Identity without isolation']
                  }, {
                    q: 'Q2',
                    title: 'Family & Relationships',
                    topics: ['Marriage and family responsibilities', 'Parenting in non-Muslim societies', 'Emotional intelligence and communication', 'Healthy boundaries and respect']
                  }, {
                    q: 'Q3',
                    title: 'Professional & Public Life',
                    topics: ['Ethics at work and in business', 'Negotiation and communication', 'Islamic finance foundations (practical)', 'Responsibility and trust']
                  }, {
                    q: 'Q4',
                    title: 'Leadership & Community',
                    topics: ['Representation and leadership', 'Conflict resolution', 'Community responsibility', 'Applied community project']
                  }].map(quarter => <div key={quarter.q} className="relative pl-6" data-id="element-1044" data-mp="16n8qtbvx9x3f">
                        <span className="absolute -left-[13px] top-0 w-6 h-6 rounded-full bg-white border-2 border-forest-700 flex items-center justify-center text-[10px] font-bold text-forest-700" data-id="element-1045" data-mp="1bgusya4gmikz">
                          {quarter.q}
                        </span>
                        <h5 className="font-display text-lg text-ink-900 leading-none mb-3 pt-1" data-id="element-1046" data-mp="1pfbakw3v1thw">
                          {quarter.title}
                        </h5>
                        <ul className="space-y-1.5" data-id="element-1047" data-mp="39vist9mo0603">
                          {quarter.topics.map(t => <li key={t} className="text-sm text-ink-600 flex items-start gap-2" data-id="element-1048" data-mp="350vda09n2lxn">
                              <span className="w-1 h-1 rounded-full bg-sage-300 mt-2 flex-shrink-0" data-id="element-1049" data-mp="2vzx99hz7aef2" />
                              {t}
                            </li>)}
                        </ul>
                      </div>)}
                  </div>

                  {/* 5. Pricing Module */}
                  <div className="mt-10 rounded-2xl bg-white border border-sage-200 p-6 shadow-sm" data-id="element-1050" data-mp="3ex88j2ku17ui">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-500 mb-2" data-id="element-1051" data-mp="g783loqn8bhp">
                      Tuition
                    </p>
                    <div className="flex items-baseline gap-2 mb-1" data-id="element-1052" data-mp="bdwuti29tkn5">
                      <span className="font-display text-3xl text-ink-900" data-id="element-1053" data-mp="ucrvetj1v4hg">
                        €1,200
                      </span>
                      <span className="text-ink-500 text-sm" data-id="element-1054" data-mp="zd5sjgj44u0p">
                        / year
                      </span>
                    </div>

                    <div className="space-y-3 mb-6" data-id="element-1055" data-mp="pkglqyyic4bp">
                      <div className="flex items-center gap-2 text-sm text-ink-700" data-id="element-1056" data-mp="blqmpds4nukp">
                        <AwardIcon size={14} className="text-forest-700" data-id="element-1057" data-mp="bntjcgnsclye" />
                        <span data-id="element-1058" data-mp="gmemanuf9jqj">Certificate of completion</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-ink-700" data-id="element-1059" data-mp="2mdlt9i2kebl">
                        <UsersIcon size={14} className="text-forest-700" data-id="element-1060" data-mp="6np3msupbv98" />
                        <span data-id="element-1061" data-mp="1wzcyouv2euf6">Small, dedicated cohorts</span>
                      </div>
                      <div className="flex items-start gap-2 text-sm text-ink-700" data-id="element-1062" data-mp="210oniy647bmb">
                        <HeartIcon size={14} className="text-forest-700 mt-0.5 flex-shrink-0" data-id="element-1063" data-mp="20ycbf1n1fvh6" />
                        <span data-id="element-1064" data-mp="3hip5drerizfh">
                          Limited scholarships available through community
                          partnerships
                        </span>
                      </div>
                    </div>

                    <Link to="/waitlist" className="block w-full" data-id="element-1065" data-mp="3qfzcr1ogm4f4">
                      <Button variant="primary" className="w-full justify-center" data-id="element-1066" data-mp="8zrc3qswhjw5">
                        Reserve your seat
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Professional Muslim Leadership Track */}
      <section id="professional-leadership" className="relative py-16 md:py-24 overflow-hidden isolate scroll-mt-24 bg-[#F7EDE9]/60" data-id="element-1067" data-mp="2jv6ti7pt1h79">
        <PatternDecor side="left" y="middle" size="md" data-id="element-1068" data-mp="1txte5ia7fd15" />
        <Container data-id="element-1069" data-mp="1xzy9clgawhpw">
          <div className="rounded-3xl bg-white border border-sage-200 shadow-soft overflow-hidden" data-id="element-1070" data-mp="1xzesl82dzzdg">
            {/* Header Band */}
            <div className="px-7 md:px-10 py-8 bg-sage-50 border-b border-sage-200 flex flex-col md:flex-row md:items-center justify-between gap-6" data-id="element-1071" data-mp="22romt0ajbllj">
              <div data-id="element-1072" data-mp="1b08qufn38min">
                <div className="flex items-center gap-3 mb-3" data-id="element-1073" data-mp="1b0s95xgnhcx7">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-forest-100 text-forest-800 text-xs font-semibold tracking-wide" data-id="element-1074" data-mp="1f12f5a4g03c2">
                    12 Months
                  </span>
                  <span className="text-ink-500 text-sm font-medium" data-id="element-1075" data-mp="1oz8d5gn4opbn">
                    Annual Professional Program
                  </span>
                </div>
                <h2 className="font-display text-3xl md:text-4xl text-ink-900" data-id="element-1076" data-mp="2vhohk6bbniao">
                  Professional Muslim Leadership Track
                </h2>
              </div>
              <div className="flex flex-col md:items-end gap-3" data-id="element-1077" data-mp="oqs8qyedmj63">
                <div className="text-right" data-id="element-1078" data-mp="jrp4vha4uizp">
                  <p className="text-2xl font-display text-forest-700" data-id="element-1079" data-mp="jt0gg39tkv1d">
                    €1,800{' '}
                    <span className="text-base text-ink-500 font-sans" data-id="element-1080" data-mp="ezfgi5oo1qzj">
                      / year
                    </span>
                  </p>
                </div>
                <Link to="/waitlist" data-id="element-1081" data-mp="61lmj1edj9fj">
                  <Button variant="primary" data-id="element-1082" data-mp="5yhajhuo39eb">Reserve your seat</Button>
                </Link>
              </div>
            </div>

            <div className="grid lg:grid-cols-12 gap-0" data-id="element-1083" data-mp="3s7iinod2xgd2">
              {/* Left Column: Content */}
              <div className="lg:col-span-7 px-7 md:px-10 py-10 border-b lg:border-b-0 lg:border-r border-sage-200" data-id="element-1084" data-mp="3n9h0d5nh9l2n">
                {/* 1. Outcome-first framing */}
                <h3 className="text-xl md:text-2xl font-display text-ink-900 leading-snug" data-id="element-1085" data-mp="1qeb62moupcuv">
                  In 12 months, elevate your leadership — combining ethical
                  Islamic principles with modern business and communication
                  skills.
                </h3>
                <p className="mt-4 text-ink-700 leading-relaxed" data-id="element-1086" data-mp="29v5gd28g261j">
                  This program is designed for Muslim professionals,
                  entrepreneurs, educators, and community leaders who wish to
                  lead ethically, communicate effectively, and engage
                  confidently in professional and public spaces.
                </p>
                <p className="mt-3 text-ink-500 italic text-sm" data-id="element-1087" data-mp="1r1t73h7py9s1">
                  It combines Islamic ethical principles with modern leadership
                  and business practices.
                </p>

                {/* 3a. Who it's for (Chips) */}
                <div className="mt-10" data-id="element-1088" data-mp="3gh92ltspzwzg">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-4" data-id="element-1089" data-mp="3gg7rjpbpfahq">
                    Who this is for
                  </h4>
                  <div className="flex flex-wrap gap-2" data-id="element-1090" data-mp="y8o9gar1z3go">
                    {['Entrepreneurs & business owners', 'Managers & team leaders', 'Educators & mentors', 'Community coordinators'].map(chip => <span key={chip} className="inline-flex items-center px-3 py-1.5 rounded-lg bg-sand-50 border border-sand-200 text-ink-700 text-sm" data-id="element-1091" data-mp="1byhxiquqhjpc">
                        {chip}
                      </span>)}
                  </div>
                </div>

                {/* 3b. Format (Icon row) */}
                <div className="mt-10" data-id="element-1092" data-mp="ta4lk4swfhlo">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-4" data-id="element-1093" data-mp="t6yy0tzx4ns2">
                    Format
                  </h4>
                  <div className="grid sm:grid-cols-2 gap-4" data-id="element-1094" data-mp="24n75n9u7ipal">
                    {[{
                    icon: CalendarIcon,
                    text: 'Monthly intensive sessions'
                  }, {
                    icon: UsersIcon,
                    text: 'Peer leadership circles'
                  }, {
                    icon: MessageCircleIcon,
                    text: 'Optional mentoring'
                  }, {
                    icon: MicIcon,
                    text: 'Expert guest practitioners'
                  }].map(f => <div key={f.text} className="flex items-center gap-3" data-id="element-1095" data-mp="3rs9onov97anb">
                        <span className="w-8 h-8 rounded-full bg-sage-100 text-forest-700 flex items-center justify-center flex-shrink-0" data-id="element-1096" data-mp="3myx8820qaa5n">
                          <f.icon size={14} strokeWidth={2} data-id="element-1097" data-mp="2z8b4uo1o5e1z" />
                        </span>
                        <span className="text-sm text-ink-700" data-id="element-1098" data-mp="2u96u3sz3j89j">{f.text}</span>
                      </div>)}
                  </div>
                </div>

                {/* 3c. Outcomes */}
                <div className="mt-10 bg-sage-50 rounded-2xl p-6 border border-sage-200" data-id="element-1099" data-mp="1elti11rblhi3">
                  <h4 className="font-display text-lg text-ink-900 mb-4" data-id="element-1100" data-mp="10qhcs0fjg8mx">
                    Where you'll be in 12 months
                  </h4>
                  <ul className="space-y-3" data-id="element-1101" data-mp="1sji2w9le0on5">
                    {['Advanced leadership skills', 'Ethical business understanding', 'Professional network', 'Certificate of completion'].map(item => <li key={item} className="flex items-start gap-3 text-ink-800 font-medium" data-id="element-1102" data-mp="1oh3l7stgfuxg">
                        <CheckCircle2Icon size={18} className="text-forest-700 mt-0.5 flex-shrink-0" strokeWidth={2} data-id="element-1103" data-mp="d2q2f2dk65oa" />
                        {item}
                      </li>)}
                  </ul>
                </div>
              </div>

              {/* Right Column: Timeline & Pricing */}
              <div className="lg:col-span-5 bg-[#FAFAFA]" data-id="element-1104" data-mp="em13uifmabfg">
                <div className="px-7 md:px-10 py-10" data-id="element-1105" data-mp="ejewojwn1xxr">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-6" data-id="element-1106" data-mp="1ba7epn34u026">
                    Program Journey
                  </h4>

                  {/* 4. Quarter Timeline */}
                  <div className="relative border-l border-sage-200 ml-3 space-y-8 pb-4" data-id="element-1107" data-mp="1299ab3os7qj2">
                    {[{
                    q: 'Q1',
                    title: 'Leadership & Ethics',
                    topics: ['Leadership as responsibility', 'Integrity and trust', 'Ethical decision-making']
                  }, {
                    q: 'Q2',
                    title: 'Business & Negotiation',
                    topics: ['Working with non-Muslim partners', 'Contracts and partnerships', 'Negotiation strategies']
                  }, {
                    q: 'Q3',
                    title: 'Finance & Responsibility',
                    topics: ['Islamic finance principles (practical)', 'Ethical investment and business models', 'Financial transparency']
                  }, {
                    q: 'Q4',
                    title: 'Community Impact',
                    topics: ['Leadership in community settings', 'Public communication', 'Impact projects']
                  }].map(quarter => <div key={quarter.q} className="relative pl-6" data-id="element-1108" data-mp="2c5eqpwwos4zz">
                        <span className="absolute -left-[13px] top-0 w-6 h-6 rounded-full bg-white border-2 border-forest-700 flex items-center justify-center text-[10px] font-bold text-forest-700" data-id="element-1109" data-mp="284l3l5r2iu85">
                          {quarter.q}
                        </span>
                        <h5 className="font-display text-lg text-ink-900 leading-none mb-3 pt-1" data-id="element-1110" data-mp="2vw08956bhnmk">
                          {quarter.title}
                        </h5>
                        <ul className="space-y-1.5" data-id="element-1111" data-mp="34wex2eihpkut">
                          {quarter.topics.map(t => <li key={t} className="text-sm text-ink-600 flex items-start gap-2" data-id="element-1112" data-mp="2zxlp11lz2m21">
                              <span className="w-1 h-1 rounded-full bg-sage-300 mt-2 flex-shrink-0" data-id="element-1113" data-mp="3oi6wjmdv7e2b" />
                              {t}
                            </li>)}
                        </ul>
                      </div>)}
                  </div>

                  {/* 5. Pricing Module */}
                  <div className="mt-10 rounded-2xl bg-white border border-sage-200 p-6 shadow-sm" data-id="element-1114" data-mp="30rkzsr9b9g7m">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-500 mb-2" data-id="element-1115" data-mp="35k4g3vzdupg4">
                      Tuition
                    </p>
                    <div className="flex items-baseline gap-2 mb-1" data-id="element-1116" data-mp="2vt9sooaehjt3">
                      <span className="font-display text-3xl text-ink-900" data-id="element-1117" data-mp="2vrgm18zsezl3">
                        €1,800
                      </span>
                      <span className="text-ink-500 text-sm" data-id="element-1118" data-mp="2mtcy661bxqq4">
                        / year
                      </span>
                    </div>

                    <div className="space-y-3 mb-6" data-id="element-1119" data-mp="32a37c6xieqy2">
                      <div className="flex items-center gap-2 text-sm text-ink-700" data-id="element-1120" data-mp="2xb9u8fm4x1yf">
                        <AwardIcon size={14} className="text-forest-700" data-id="element-1121" data-mp="2sceykd32jiww" />
                        <span data-id="element-1122" data-mp="2sernxbb5ovjz">Certificate of completion</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-ink-700" data-id="element-1123" data-mp="2oato0ye0bgpv">
                        <UsersIcon size={14} className="text-forest-700" data-id="element-1124" data-mp="2jdl52vl1b8nk" />
                        <span data-id="element-1125" data-mp="2ek8w1tsu9afx">Peer leadership network</span>
                      </div>
                      <div className="flex items-start gap-2 text-sm text-ink-700" data-id="element-1126" data-mp="29lnqrfyqc0km">
                        <TargetIcon size={14} className="text-forest-700 mt-0.5 flex-shrink-0" data-id="element-1127" data-mp="24ndzhgg4lk4a" />
                        <span data-id="element-1128" data-mp="20nloyy7693wa">Admission by application</span>
                      </div>
                    </div>

                    <Link to="/waitlist" className="block w-full" data-id="element-1129" data-mp="b4dlonh3w9qg">
                      <Button variant="primary" className="w-full justify-center" data-id="element-1130" data-mp="b43w30zz44la">
                        Reserve your seat
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Educator & Mentor Path */}
      <section id="educator-mentor" className="py-16 md:py-24 scroll-mt-24 bg-sand-50/60" data-id="element-1131" data-mp="3ohgr8guwzuut">
        <Container data-id="element-1132" data-mp="1eu3lk17wjwn">
          <div className="rounded-3xl bg-white border border-sage-200 shadow-soft overflow-hidden" data-id="element-1133" data-mp="1ebxlvor6rbj">
            {/* Header Band */}
            <div className="px-7 md:px-10 py-8 bg-sage-50 border-b border-sage-200 flex flex-col md:flex-row md:items-center justify-between gap-6" data-id="element-1134" data-mp="66vasrjj3kft">
              <div data-id="element-1135" data-mp="39u42kj9t9907">
                <div className="flex items-center gap-3 mb-3" data-id="element-1136" data-mp="39tlzp3sn7wjb">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-forest-100 text-forest-800 text-xs font-semibold tracking-wide" data-id="element-1137" data-mp="2jkgjxdf4q6ny">
                    12 Months
                  </span>
                  <span className="text-ink-500 text-sm font-medium" data-id="element-1138" data-mp="2ak1x29miojic">
                    Annual Certification Program
                  </span>
                </div>
                <h2 className="font-display text-3xl md:text-4xl text-ink-900" data-id="element-1139" data-mp="2fh1vpzh1ejzf">
                  Educator & Mentor Path
                </h2>
              </div>
              <div className="flex flex-col md:items-end gap-3" data-id="element-1140" data-mp="25lqo0141qd16">
                <div className="text-right" data-id="element-1141" data-mp="1rtadkf8tfcq8">
                  <p className="text-2xl font-display text-forest-700" data-id="element-1142" data-mp="1ruli4ha34oie">
                    €1,600{' '}
                    <span className="text-base text-ink-500 font-sans" data-id="element-1143" data-mp="1vvf7lf36ebde">
                      / year
                    </span>
                  </p>
                </div>
                <Link to="/waitlist" data-id="element-1144" data-mp="1muh0up1crpr4">
                  <Button variant="primary" data-id="element-1145" data-mp="1mtfp0iciiz8c">Reserve your seat</Button>
                </Link>
              </div>
            </div>

            <div className="grid lg:grid-cols-12 gap-0" data-id="element-1146" data-mp="y9cckqvjqfee">
              {/* Left Column: Content */}
              <div className="lg:col-span-7 px-7 md:px-10 py-10 border-b lg:border-b-0 lg:border-r border-sage-200" data-id="element-1147" data-mp="1d6lkow92jxst">
                {/* 1. Outcome-first framing */}
                <h3 className="text-xl md:text-2xl font-display text-ink-900 leading-snug" data-id="element-1148" data-mp="18678d20fq1oz">
                  In 12 months, become a certified educator — equipped to
                  support Muslim children with confidence, ethics, and care.
                </h3>
                <p className="mt-4 text-ink-700 leading-relaxed" data-id="element-1149" data-mp="1vxcpdki4300v">
                  A professional certification program for educators, mentors,
                  and community workers who support Muslim children and families
                  in non-Muslim countries.
                </p>
                <p className="mt-3 text-ink-500 italic text-sm" data-id="element-1150" data-mp="20wpkesj4zdej">
                  The program focuses on child development, safeguarding,
                  ethical teaching, and responsible community work with strong Islamic values.
                </p>

                {/* 3a. Who it's for (Chips) */}
                <div className="mt-10" data-id="element-1151" data-mp="2hxa8cluoky0f">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-4" data-id="element-1152" data-mp="2hw8yultbcjkt">
                    Who this is for
                  </h4>
                  <div className="flex flex-wrap gap-2" data-id="element-1153" data-mp="2qou36ww76nq4">
                    {['Teachers & educators', 'Homeschool parents', 'Youth mentors', 'Community workers'].map(chip => <span key={chip} className="inline-flex items-center px-3 py-1.5 rounded-lg bg-sand-50 border border-sand-200 text-ink-700 text-sm" data-id="element-1154" data-mp="35ne98lghijnq">
                        {chip}
                      </span>)}
                  </div>
                </div>

                {/* 3b. Format (Icon row) */}
                <div className="mt-10" data-id="element-1155" data-mp="3jseqiulklqjh">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-4" data-id="element-1156" data-mp="3jqk0x2zcta92">
                    Format
                  </h4>
                  <div className="grid sm:grid-cols-2 gap-4" data-id="element-1157" data-mp="3at1cdomi4dsf">
                    {[{
                    icon: UsersIcon,
                    text: 'Live interactive sessions'
                  }, {
                    icon: TargetIcon,
                    text: 'Supervised practicum'
                  }, {
                    icon: MessageCircleIcon,
                    text: 'Assessments & feedback'
                  }, {
                    icon: HeartIcon,
                    text: 'Peer support groups'
                  }].map(f => <div key={f.text} className="flex items-center gap-3" data-id="element-1158" data-mp="9ttcvdlwqz6t">
                        <span className="w-8 h-8 rounded-full bg-sage-100 text-forest-700 flex items-center justify-center flex-shrink-0" data-id="element-1159" data-mp="ell29ktbuyvd">
                          <f.icon size={14} strokeWidth={2} data-id="element-1160" data-mp="vre74pncimp" />
                        </span>
                        <span className="text-sm text-ink-700" data-id="element-1161" data-mp="5tj8g5drkgu2">{f.text}</span>
                      </div>)}
                  </div>
                </div>

                {/* 3c. Outcomes */}
                <div className="mt-10 bg-sage-50 rounded-2xl p-6 border border-sage-200" data-id="element-1162" data-mp="xje4ekw4n4rp">
                  <h4 className="font-display text-lg text-ink-900 mb-4" data-id="element-1163" data-mp="jk64fps53smg">
                    Where you'll be in 12 months
                  </h4>
                  <ul className="space-y-3" data-id="element-1164" data-mp="34awxgg55oh13">
                    {['Educator certification', 'Eligibility for partner programs', 'Safeguarding expertise', 'Certificate of completion'].map(item => <li key={item} className="flex items-start gap-3 text-ink-800 font-medium" data-id="element-1165" data-mp="2wrxci621qhfm">
                        <CheckCircle2Icon size={18} className="text-forest-700 mt-0.5 flex-shrink-0" strokeWidth={2} data-id="element-1166" data-mp="35rsr6kut8uke" />
                        {item}
                      </li>)}
                  </ul>
                </div>
              </div>

              {/* Right Column: Timeline & Pricing */}
              <div className="lg:col-span-5 bg-[#FAFAFA]" data-id="element-1167" data-mp="2n0syp9o3x2md">
                <div className="px-7 md:px-10 py-10" data-id="element-1168" data-mp="2mz845k1sljoi">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-6" data-id="element-1169" data-mp="1zaoub033gyub">
                    Program Journey
                  </h4>

                  {/* 4. Quarter Timeline */}
                  <div className="relative border-l border-sage-200 ml-3 space-y-8 pb-4" data-id="element-1170" data-mp="3ue3ozvr9tuxw">
                    {[{
                    q: 'Q1',
                    title: 'Child Development',
                    topics: ['Emotional and cognitive development', 'Age-appropriate learning']
                  }, {
                    q: 'Q2',
                    title: 'Teaching Values Gently',
                    topics: ['Islamic values', 'Communication and trust']
                  }, {
                    q: 'Q3',
                    title: 'Safeguarding & Ethics',
                    topics: ['Child protection standards', 'Boundaries and responsibility']
                  }, {
                    q: 'Q4',
                    title: 'Practicum & Assessment',
                    topics: ['Supervised teaching practice', 'Reflection and evaluation']
                  }].map(quarter => <div key={quarter.q} className="relative pl-6" data-id="element-1171" data-mp="26hx8j50syj45">
                        <span className="absolute -left-[13px] top-0 w-6 h-6 rounded-full bg-white border-2 border-forest-700 flex items-center justify-center text-[10px] font-bold text-forest-700" data-id="element-1172" data-mp="21hj8oa9cufh7">
                          {quarter.q}
                        </span>
                        <h5 className="font-display text-lg text-ink-900 leading-none mb-3 pt-1" data-id="element-1173" data-mp="1sjph1cmjca0d">
                          {quarter.title}
                        </h5>
                        <ul className="space-y-1.5" data-id="element-1174" data-mp="3mvx300gvpgk0">
                          {quarter.topics.map(t => <li key={t} className="text-sm text-ink-600 flex items-start gap-2" data-id="element-1175" data-mp="2vqtdvzqze33f">
                              <span className="w-1 h-1 rounded-full bg-sage-300 mt-2 flex-shrink-0" data-id="element-1176" data-mp="2lzp033d1koa6" />
                              {t}
                            </li>)}
                        </ul>
                      </div>)}
                  </div>

                  {/* 5. Pricing Module */}
                  <div className="mt-10 rounded-2xl bg-white border border-sage-200 p-6 shadow-sm" data-id="element-1177" data-mp="231ch6pwbw01e">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-500 mb-2" data-id="element-1178" data-mp="280fibz96tm43">
                      Tuition
                    </p>
                    <div className="flex items-baseline gap-2 mb-1" data-id="element-1179" data-mp="1aslr9kpevr6">
                      <span className="font-display text-3xl text-ink-900" data-id="element-1180" data-mp="6815heli37uj">
                        €1,600
                      </span>
                      <span className="text-ink-500 text-sm" data-id="element-1181" data-mp="1qrehvlrsjcfb">
                       / year
                      </span>
                    </div>

                    <div className="space-y-3 mb-6" data-id="element-1182" data-mp="20hg56bcbkqt6">
                      <div className="flex items-center gap-2 text-sm text-ink-700" data-id="element-1183" data-mp="20j0xdub5vxnv">
                        <AwardIcon size={14} className="text-forest-700" data-id="element-1184" data-mp="1vomvra5wouar" />
                        <span data-id="element-1185" data-mp="29gl25d88gc38">Educator certification</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-ink-700" data-id="element-1186" data-mp="24jb3r82v8q39">
                        <TargetIcon size={14} className="text-forest-700" data-id="element-1187" data-mp="2jfipictx9zkn" />
                        <span data-id="element-1188" data-mp="2jdfxjn97lwda">Supervised practicum</span>
                      </div>
                    </div>

                    <Link to="/waitlist" className="block w-full" data-id="element-1189" data-mp="p1rhtiv5lpru">
                      <Button variant="primary" className="w-full justify-center" data-id="element-1190" data-mp="p216n34gpqvy">
                        Reserve your seat
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Program features */}
      <section className="relative py-16 md:py-24 overflow-hidden isolate bg-white/60" data-id="element-1191" data-mp="1uyub5d8gwij7">
        <PatternDecor side="right" y="top" size="lg" data-id="element-1192" data-mp="1q46we639n2ht" />
        <Container data-id="element-1193" data-mp="2413k3yo383uh">
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-1194" data-mp="240kb57gf0psl">
            How Annual Programs work
          </h2>
          <p className="mt-5 text-ink-700 leading-relaxed" data-id="element-1195" data-mp="2drosgdtkcmql">
            A consistent rhythm of structured monthly content, live sessions,
            and personal practice — designed for busy adults.
          </p>
          <div className="mt-10 grid md:grid-cols-3 gap-5" data-id="element-1196" data-mp="2ms3ejfecq5v5">
            {features.map((f, i) => {
            const tones = ['bg-sage-100', 'bg-[#F7EDE9]', 'bg-sand-100'];
            return <motion.div key={f.title} initial={{
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-60px'
            }} transition={{
              duration: 0.5,
              delay: i * 0.06
            }} className={`rounded-2xl ${tones[i % tones.length]} border border-ink-900/5 p-6 shadow-soft`} data-id="element-1197" data-mp="31fblu54jhr1u" data-mpi={i}>
                  <span className="w-11 h-11 inline-flex items-center justify-center rounded-xl bg-white text-forest-700" data-id="element-1198" data-mp="29pqkxrm2w0gr">
                    <f.icon size={18} strokeWidth={1.6} data-id="element-1199" data-mp="2dn4whpx8ekbx" />
                  </span>
                  <h3 className="mt-5 font-display text-lg text-ink-900" data-id="element-1200" data-mp="1zrsyn9ns4dqs">
                    {f.title}
                  </h3>
                  <p className="mt-2 text-ink-700 text-sm leading-relaxed" data-id="element-1201" data-mp="a8caajrft7yo">
                    {f.body}
                  </p>
                </motion.div>;
          })}
          </div>
        </Container>
      </section>

      {/* Access & Scholarships */}
      <section className="relative py-16 md:py-24 overflow-hidden isolate bg-sage-50/60" data-id="element-1202" data-mp="1e6b0hybtiuz">
        <PatternDecor side="right" y="bottom" size="md" data-id="element-1203" data-mp="3soesut2fyz31" />
        <Container data-id="element-1204" data-mp="t7q9bg40x8w1">
          <h3 className="font-display text-lg text-ink-900" data-id="element-1205" data-mp="t65f5rlmfryp">
            Access & Scholarships
          </h3>
          <p className="mt-4 text-ink-700 leading-relaxed" data-id="element-1206" data-mp="p6d0cx5mwpkw">
            Some cohorts may include limited scholarship places through
            community partnerships to ensure our programs remain accessible to
            motivated learners.
          </p>
        </Container>
      </section>

      {/* Educational disclaimer */}
      <section className="py-10 md:py-14 bg-white/60" data-id="element-1207" data-mp="34umvblfwcaws">
        <Container data-id="element-1208" data-mp="2zx4u6h84ezzc">
          <aside aria-labelledby="annual-programs-disclaimer" className="rounded-2xl border border-[#E0BFB3]/60 bg-[#F7EDE9] px-6 py-5 md:px-8 md:py-6" data-id="element-1209" data-mp="2zwlaauywu1io">
            <h2 id="annual-programs-disclaimer" className="font-display text-lg text-ink-900" data-id="element-1210" data-mp="2utkhfz7650zw">
              Disclaimer
            </h2>
            <p className="mt-3 max-w-5xl text-sm leading-relaxed text-ink-700" data-id="element-1211" data-mp="34jxc4k8p6ajr">
              Our programs provide general, applied educational content.
            </p>
            <ul className="mt-4 max-w-5xl list-disc space-y-3 pl-5 text-sm leading-relaxed text-ink-700 marker:text-ink-900" data-id="element-1212" data-mp="3djab2iz73gq8">
              <li className="pl-1" data-id="element-1213" data-mp="3ijolu8s6l4ry">
                <strong className="text-ink-900" data-id="element-1214" data-mp="3ijexepmsb5od">
                  Personal &amp; Family Matters:
                </strong>{' '}
                For specific legal or personal rulings (<em data-id="element-1215" data-mp="1o5nl1id2m7p8">fatwas</em>)
                regarding marriage (<em data-id="element-1216" data-mp="1t3p628u24h3c">nikah</em>), divorce, or inheritance,
                please consult a qualified Islamic scholar or a recognized
                Sharia council. We are glad to help facilitate a connection to
                trusted scholarly authorities upon request.
              </li>
              <li className="pl-1" data-id="element-1217" data-mp="3eo2c3lued7zm">
                <strong className="text-ink-900" data-id="element-1218" data-mp="3sm0lsdxrwg8s">
                  Business &amp; Finance:
                </strong>{' '}
                Professional, financial, and business-related content is
                strictly educational and practical in nature and does not
                constitute regulated legal, tax, or financial advice.
              </li>
            </ul>
          </aside>
        </Container>
      </section>

      {/* CTA */}
      <section className="relative py-16 md:py-24 overflow-hidden isolate bg-sage-50/60" data-id="element-1219" data-mp="39nxtnq9xi36r">
        <PatternDecor side="right" y="bottom" size="md" data-id="element-1220" data-mp="34ud6yudscvmv" />
        <Container data-id="element-1221" data-mp="2h5tv64vj20q1">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8" data-id="element-1222" data-mp="2h5afyvx9hqfp">
            <div className="max-w-xl" data-id="element-1223" data-mp="2c67jhzvcksjc">
              <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-1224" data-mp="2c5681uaf6413">
                Start your Annual Program
              </h2>
              <p className="mt-4 text-ink-700" data-id="element-1225" data-mp="wg6lla00vedl">
                Request information, ask questions, and choose the best pathway
                for your goals.
              </p>
            </div>
            <div className="flex flex-wrap gap-3" data-id="element-1226" data-mp="nmtm1p7gdeij">
              <Link to="/en/waitlist" data-id="element-1227" data-mp="nlijtttnoetj">
                <Button variant="primary" data-id="element-1228" data-mp="1bcnza7vop53b">Join</Button>
              </Link>
              <Link to="/en/contact" data-id="element-1229" data-mp="1feso8tb6tdjm">
                <Button variant="secondary" data-id="element-1230" data-mp="1fdrcemmckn0u">Request Info</Button>
              </Link>
            </div>
          </div>
        </Container>
      </section>
    </>;
}